import { useState, useRef, useEffect } from 'react';
import { sans, mono, green, greenLight, greenDark, useTheme } from '../tokens';

const ANTHROPIC_KEY = process.env.REACT_APP_ANTHROPIC_API_KEY;

const SYSTEM_PROMPT = `You are a financial planning assistant embedded in FinKloz, a CFO platform for Demo Medical Devices — a medtech company developing a cardiac device.

When the user describes a financial scenario change, extract structured adjustments.

Each adjustment has:
- category: one of payroll, rdMfg, rdClinical, professional, facilities, softwareIT, hrPeople, depreciation, interest, grants, revenue
- action: "add" (increase by $/yr), "reduce" (decrease by $/yr), or "multiply" (scale by percentage)
- amount: the dollar amount per year (for add/reduce) or percentage (for multiply, e.g. 130 = 130% of current)
- start_year: the year the change begins (2026-2031)

Current annual figures (illustrative):
- payroll: $6.4M (50 employees, avg ~$125K/employee fully loaded)
- rdMfg: $3.3M (motors, components, consumables, tooling)
- rdClinical: $640K (preclinical and clinical studies)
- professional: $1.0M (legal, patents, audit, insurance)
- facilities: $560K (rent, maintenance)
- softwareIT: $880K (subscriptions, IT equipment)
- hrPeople: $420K (recruiting, events, benefits admin)
- depreciation: $700K
- interest: $1.8M (treasury income)
- grants: $85K
- revenue: $0

CRITICAL RULES:
- ALWAYS respond in the same language as the user's message.
- ALWAYS respond with ONLY a valid JSON object, no markdown, no text before or after.
- If the user asks a general question or asks for advice, propose concrete adjustments as suggestions in the adjustments array and explain in the interpretation field.
- If no specific adjustment can be extracted, return an empty adjustments array with a helpful interpretation.
- NEVER use markdown formatting. ONLY raw JSON.

Respond with ONLY this JSON format:
{
  "adjustments": [
    { "category": "...", "action": "...", "amount": ..., "start_year": ... }
  ],
  "interpretation": "Brief friendly summary of what you understood and the financial impact, in 2-3 sentences max"
}`;

const CATEGORY_LABELS = {
  payroll: 'Payroll',
  rdMfg: 'R&D Manufacturing',
  rdClinical: 'R&D Clinical',
  professional: 'Professional Services',
  facilities: 'Facilities',
  softwareIT: 'Software & IT',
  hrPeople: 'HR & People',
  depreciation: 'Depreciation',
  interest: 'Interest Income',
  grants: 'Grants',
  revenue: 'Revenue',
};

const ACTION_LABELS = { add: '+', reduce: '-', multiply: 'x' };

function fmtAmount(action, amount) {
  if (action === 'multiply') return `${amount}%`;
  if (amount >= 1000000) return `$${(amount / 1000000).toFixed(1)}M`;
  if (amount >= 1000) return `$${(amount / 1000).toFixed(0)}K`;
  return `$${amount}`;
}

function buildSystemPrompt(forecastData, customAdjustments) {
  let forecastContext = '';
  if (forecastData && forecastData.length > 0) {
    forecastContext = '\n\nCurrent forecast data by year:\n' + forecastData.map(d =>
      `${d.year}: Revenue=$${(d.revenue/1000).toFixed(0)}K | Payroll=$${(d.payroll/1000000).toFixed(1)}M | R&D Mfg=$${(d.rdMfg/1000000).toFixed(1)}M | R&D Clinical=$${(d.rdClinical/1000).toFixed(0)}K | Professional=$${(d.professional/1000000).toFixed(1)}M | Facilities=$${(d.facilities/1000).toFixed(0)}K | Software&IT=$${(d.softwareIT/1000).toFixed(0)}K | HR=$${(d.hrPeople/1000).toFixed(0)}K | Depreciation=$${(d.depreciation/1000).toFixed(0)}K | Total Expenses=$${(d.totalExpenses/1000000).toFixed(1)}M | Net Burn=$${(d.netBurn/1000000).toFixed(1)}M | Cash End=$${(d.cashEnd/1000000).toFixed(1)}M`
    ).join('\n');
  }
  if (customAdjustments && customAdjustments.length > 0) {
    forecastContext += '\n\nCurrently applied adjustments:\n' + customAdjustments.map(a =>
      `- ${a.category}: ${a.action} $${(a.amount/1000).toFixed(0)}K from ${a.startYear}`
    ).join('\n');
  }
  return SYSTEM_PROMPT + forecastContext;
}

export default function ScenarioChat({ forecastData, customAdjustments, onApplyAdjustments }) {
  const t = useTheme();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedAdjs, setSelectedAdjs] = useState({}); // { msgIndex: Set of adj indices }
  const chatEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (chatEndRef.current) {
      const container = chatEndRef.current.parentElement;
      container.scrollTop = container.scrollHeight;
    }
  }, [messages]);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || loading) return;

    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: text }]);
    setLoading(true);

    try {
      const res = await fetch('https://api.anthropic.com/v1/messages', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': ANTHROPIC_KEY,
          'anthropic-version': '2023-06-01',
          'anthropic-dangerous-direct-browser-access': 'true',
        },
        body: JSON.stringify({
          model: 'claude-haiku-4-5-20251001',
          max_tokens: 1024,
          system: buildSystemPrompt(forecastData, customAdjustments),
          messages: [{ role: 'user', content: text }],
        }),
      });

      if (!res.ok) {
        const err = await res.text();
        throw new Error(`API error ${res.status}: ${err}`);
      }

      const data = await res.json();
      const raw = data.content[0].text;

      let parsed;
      try {
        parsed = JSON.parse(raw);
      } catch {
        const match = raw.match(/```(?:json)?\s*(\{[\s\S]*?\})\s*```/);
        if (match) parsed = JSON.parse(match[1]);
        else parsed = { adjustments: [], interpretation: raw.slice(0, 200) };
      }

      setMessages(prev => {
        const newIdx = prev.length;
        const adjs = parsed.adjustments || [];
        if (adjs.length > 0) {
          setSelectedAdjs(sa => ({ ...sa, [newIdx]: new Set(adjs.map((_, i) => i)) }));
        }
        return [...prev, {
          role: 'assistant',
          content: parsed.interpretation,
          adjustments: adjs,
        }];
      });
    } catch (err) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `Error: ${err.message}`,
        adjustments: [],
      }]);
    }
    setLoading(false);
    inputRef.current?.focus();
  };

  const handleApply = (adjustments) => {
    onApplyAdjustments(adjustments);
    setMessages(prev => [...prev, {
      role: 'system',
      content: `${adjustments.length} adjustment(s) applied to forecast.`,
    }]);
  };

  return (
    <div style={{
      border: `1.5px solid ${green}40`, borderRadius: 10, overflow: 'hidden',
      display: 'flex', flexDirection: 'column', height: 240,
      boxShadow: `0 2px 12px ${green}12`,
    }}>
      {/* Header */}
      <div style={{
        padding: '10px 14px', background: `linear-gradient(135deg, ${greenLight}, ${green}15)`,
        borderBottom: `1px solid ${green}30`,
        display: 'flex', alignItems: 'center', gap: 8,
      }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={green} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        </svg>
        <span style={{ fontSize: 12, fontWeight: 700, color: greenDark, fontFamily: sans }}>
          KlozPilot
        </span>
      </div>

      {/* Messages */}
      <div style={{
        flex: 1, overflowY: 'auto', padding: '10px 12px',
        display: 'flex', flexDirection: 'column', gap: 8,
        background: t.bg,
      }}>
        {messages.length === 0 && (
          <div style={{ textAlign: 'center', padding: '20px 10px', color: t.textMuted, fontSize: 11, fontFamily: sans }}>
            <div style={{ marginBottom: 8, fontSize: 12, fontWeight: 700 }}>Add a scenario</div>
            <div style={{ color: t.textFaint, fontSize: 10, lineHeight: 1.6 }}>
              "If I hire 3 engineers at $130K starting 2027"<br />
              "What if clinical trial costs increase by $500K/yr"<br />
              "Reduce IT expenses by 20%"
            </div>
          </div>
        )}

        {messages.map((msg, i) => (
          <div key={i} style={{
            alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start',
            maxWidth: '85%',
          }}>
            {/* Message bubble */}
            <div style={{
              padding: '7px 11px', borderRadius: 10,
              background: msg.role === 'user' ? green : msg.role === 'system' ? greenLight : t.bgSurface,
              color: msg.role === 'user' ? '#fff' : msg.role === 'system' ? greenDark : t.textPrimary,
              fontSize: 11, fontFamily: sans, lineHeight: 1.5,
              border: msg.role === 'assistant' ? `1px solid ${t.border}` : 'none',
            }}>
              {msg.content}
            </div>

            {/* Adjustment cards + Apply button */}
            {msg.adjustments && msg.adjustments.length > 0 && (
              <div style={{ marginTop: 6 }}>
                {msg.adjustments.map((adj, j) => {
                  const selected = selectedAdjs[i]?.has(j) ?? true;
                  return (
                    <div key={j} onClick={() => {
                      setSelectedAdjs(sa => {
                        const set = new Set(sa[i] || []);
                        if (set.has(j)) set.delete(j); else set.add(j);
                        return { ...sa, [i]: set };
                      });
                    }} style={{
                      display: 'flex', gap: 8, alignItems: 'center',
                      padding: '5px 10px', marginBottom: 3,
                      background: selected ? t.bgSurface : t.bg, borderRadius: 6,
                      border: `1px solid ${selected ? green + '40' : t.borderLight}`,
                      fontSize: 10, fontFamily: mono, color: t.textSecondary,
                      cursor: 'pointer', opacity: selected ? 1 : 0.5,
                      transition: 'all 0.15s',
                    }}>
                      <div style={{
                        width: 14, height: 14, borderRadius: 3, flexShrink: 0,
                        border: `1.5px solid ${selected ? green : t.borderMedium}`,
                        background: selected ? green : 'transparent',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        {selected && <span style={{ color: '#fff', fontSize: 9, lineHeight: 1 }}>✓</span>}
                      </div>
                      <span style={{ fontWeight: 600, color: t.textPrimary }}>
                        {CATEGORY_LABELS[adj.category] || adj.category}
                      </span>
                      <span style={{
                        color: adj.action === 'reduce' ? '#C0392B' : green,
                        fontWeight: 600,
                      }}>
                        {ACTION_LABELS[adj.action]}{fmtAmount(adj.action, adj.amount)}
                      </span>
                      <span style={{ color: t.textMuted }}>
                        from {adj.start_year}
                      </span>
                    </div>
                  );
                })}
                <button
                  onClick={() => {
                    const sel = selectedAdjs[i];
                    const filtered = sel ? msg.adjustments.filter((_, j) => sel.has(j)) : msg.adjustments;
                    if (filtered.length > 0) handleApply(filtered);
                  }}
                  disabled={selectedAdjs[i]?.size === 0}
                  style={{
                    marginTop: 4, padding: '6px 10px', borderRadius: 4,
                    background: (selectedAdjs[i]?.size === 0) ? t.borderMedium : green,
                    color: '#fff', border: 'none',
                    fontSize: 11, fontWeight: 500, fontFamily: sans,
                    cursor: (selectedAdjs[i]?.size === 0) ? 'default' : 'pointer',
                  }}
                >
                  Apply {selectedAdjs[i] ? `(${selectedAdjs[i].size})` : `(${msg.adjustments.length})`} to forecast
                </button>
              </div>
            )}
          </div>
        ))}

        {loading && (
          <div style={{ alignSelf: 'flex-start', padding: '7px 11px', borderRadius: 10, background: t.bgSurface, border: `1px solid ${t.border}` }}>
            <span style={{ fontSize: 11, color: t.textMuted, fontFamily: sans }}>Thinking...</span>
          </div>
        )}

        <div ref={chatEndRef} />
      </div>

      {/* Input */}
      <div style={{
        display: 'flex', gap: 6, padding: '8px 10px',
        borderTop: `1px solid ${t.border}`, background: t.bgSurface,
      }}>
        <input
          ref={inputRef}
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendMessage()}
          placeholder="Describe a scenario..."
          disabled={loading}
          style={{
            flex: 1, padding: '6px 10px', borderRadius: 6,
            border: `1px solid ${t.border}`, background: t.inputBg,
            color: t.textPrimary, fontSize: 11, fontFamily: sans,
            outline: 'none',
          }}
        />
        <button
          onClick={sendMessage}
          disabled={loading || !input.trim()}
          style={{
            padding: '6px 14px', borderRadius: 6, border: 'none',
            background: input.trim() ? green : t.borderMedium,
            color: '#fff', fontSize: 11, fontWeight: 600,
            fontFamily: sans, cursor: input.trim() ? 'pointer' : 'default',
          }}
        >
          Send
        </button>
      </div>
    </div>
  );
}
